interface AnchorLinksProps {
    el: HTMLDivElement;
}

const STICKY_SCROLL_OFFSET = 160;
const NON_STICKY_SCROLL_OFFSET = 400;
const HEADING_SELECTOR = '.page-content section:not(.sg-content):not(.hero-banner):not(.level-2-banner):not(.level-3-banner) h2';

let stickyHeaderElement: HTMLDivElement | null = null;
let anchorLinksElement: HTMLDivElement | null = null;
let lastClickedAnchorText = '';

const updateActiveAnchorLink = (): void => {
    const headings = document.querySelectorAll<HTMLElement>(HEADING_SELECTOR);
    const targetY = window.innerHeight * 0.25;
    let closestHeading: HTMLElement | null = null;
    let smallestDistance = Infinity;

    headings.forEach(heading => {
        const rect = heading.getBoundingClientRect();
        const distance = Math.abs(rect.top - targetY);

        if (rect.top < window.innerHeight && distance < smallestDistance) {
            smallestDistance = distance;
            closestHeading = heading;
        }
    });

    if (closestHeading) {
        const topId = (closestHeading as HTMLElement).id;

        document.querySelectorAll('.anchor-links-item').forEach(item => {
            const link = item.querySelector<HTMLAnchorElement>('a');
            const href = link?.getAttribute('href') ?? '';

            const isActive = href === `#${topId}`;
            item.classList.toggle('active', isActive);

            if (isActive && link) {
                const anchorText = link.textContent?.trim() || '';
                lastClickedAnchorText = anchorText;

                const accordionText = anchorLinksElement
                    ?.querySelector('.accordion .accordion-heading button div');
                if (accordionText) {
                    accordionText.textContent = anchorText;
                }
            }
        });
    }
};

const observerOptions = {
    root: null,
    rootMargin: '0px 0px 0px 0px',
    threshold: 0,
};

const handleIntersections = (entries: IntersectionObserverEntry[]): void => {
    entries.forEach(entry => {
        const { id } = entry.target;
        const anchorItems = document.querySelectorAll(`.anchor-links-item a[href="#${id}"]`);

        anchorItems.forEach(anchor => {
            const parent = anchor.closest('.anchor-links-item');
            if (!parent) {
                return;
            }

            if (entry.isIntersecting) {
                parent.classList.add('active');
            } else {
                parent.classList.remove('active');
            }
        });
    });
};

const observer = new IntersectionObserver(handleIntersections, observerOptions);

document.querySelectorAll(HEADING_SELECTOR).forEach(heading => {
    observer.observe(heading);
});

const generateSlug = (text: string): string => {
    return text.toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
};

const createAnchorList = (headings: NodeListOf<HTMLHeadingElement>): HTMLUListElement => {
    const ul = document.createElement('ul');
    ul.classList.add('anchor-links-list');

    headings.forEach(heading => {
        const text = heading.textContent?.trim() || '';
        const slug = generateSlug(text);
        heading.id = slug;

        const li = document.createElement('li');
        li.classList.add('anchor-links-item');

        const anchor = document.createElement('a');
        anchor.href = `#${slug}`;
        anchor.textContent = text;
        anchor.classList.add('anchor-links-link');

        li.appendChild(anchor);
        ul.appendChild(li);
    });

    return ul;
};

const scrollToTarget = (target: HTMLElement): void => {
    requestAnimationFrame(() => {
        const offset = ((stickyHeaderElement?.offsetHeight ?? 0) + STICKY_SCROLL_OFFSET);
        const additionalPadding = anchorLinksElement?.classList.contains('sticky') ? 0 : NON_STICKY_SCROLL_OFFSET;
        const position = target.getBoundingClientRect().top + window.scrollY;

        window.scrollTo({
            top: position - offset - additionalPadding,
            behavior: 'smooth',
        });
    });
};

const handleClick = (anchorLink: Element): void => {
    const anchorText = anchorLink.textContent?.trim() || '';
    lastClickedAnchorText = anchorText;

    const accordionItem = anchorLink.closest('.accordion-item');
    const targetId = anchorLink.querySelector('a')?.getAttribute('href')
        ?.substring(1);
    const targetElement = targetId ? document.getElementById(targetId) : null;

    const openAccordionAndScroll = (): void => {
        if (!targetElement) {
            return;
        }

        // Wait for the DOM to update after accordion opens
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                scrollToTarget(targetElement);
            });
        });
    };

    if (accordionItem) {
        const button = accordionItem.querySelector<HTMLButtonElement>('.accordion-heading button');
        const label = button?.querySelector('div');

        if (label) {
            label.textContent = anchorText;
        }

        if (button) {
            button.click();
            setTimeout(openAccordionAndScroll, 200);
        } else {
            openAccordionAndScroll();
        }
    } else {
        openAccordionAndScroll();
    }
};

const handleScroll = (): void => {
    if (!anchorLinksElement || !stickyHeaderElement) {
        return;
    }

    const rect = anchorLinksElement.getBoundingClientRect();
    const stickyHeaderHeight = stickyHeaderElement.offsetHeight;
    const isSticky = rect.top <= stickyHeaderHeight;

    const expandedSection = anchorLinksElement.querySelector('.anchor-links-expanded') as HTMLDivElement;
    const accordionSection = anchorLinksElement.querySelector('.accordion') as HTMLDivElement;
    const accordionText = accordionSection?.querySelector<HTMLDivElement>('.accordion-heading button div');
    const isExpandedVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

    anchorLinksElement.classList.toggle('sticky', isSticky);
    anchorLinksElement.style.top = isSticky ? `${stickyHeaderHeight}px` : '';

    if (expandedSection && accordionSection) {
        expandedSection.setAttribute('aria-hidden', String(isSticky));
        accordionSection.setAttribute('aria-hidden', String(!isSticky));
    }

    if (!isExpandedVisible && accordionText && lastClickedAnchorText) {
        accordionText.textContent = lastClickedAnchorText;
    }
};

const handleResize = (): void => {
    if (anchorLinksElement?.classList.contains('sticky') && stickyHeaderElement) {
        anchorLinksElement.style.top = `${stickyHeaderElement.offsetHeight}px`;
    }
};

const AnchorLinks = ({ el }: AnchorLinksProps): void => {
    anchorLinksElement = el;
    stickyHeaderElement = document.querySelector('.sticky-header') as HTMLDivElement;

    const headings = document.querySelectorAll<HTMLHeadingElement>(HEADING_SELECTOR);
    const contentContainers = el.querySelectorAll('.anchor-links-content') as NodeListOf<HTMLDivElement>;

    if (headings.length < 2 || contentContainers.length === 0) {
        el.remove();
        return;
    }

    el.style.display = 'block';

    contentContainers.forEach(container => {
        const list = createAnchorList(headings);
        container.appendChild(list);
    });

    el.querySelectorAll('.anchor-links-item').forEach(anchorLink => {
        anchorLink.addEventListener('click', () => {
            handleClick(anchorLink);
        });
    });

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('scroll', updateActiveAnchorLink);
    window.addEventListener('resize', handleResize);

    handleScroll();
};

export default AnchorLinks;
